import { useMemo, useCallback, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useImportStore } from '@/stores/importStore';
import { useCategories } from '@/hooks/useCategories';
import { usePlans } from '@/hooks/usePlans';
import { useChannelTypes } from '@/hooks/useChannelTypes';
import { ParsedChannelData, ImportError, ValidationError, ProcessError, ParsedChannelNumber } from '../../types/import';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { ValidationService } from '@/services/import/validation';
import { useProvider } from '@/hooks/useProvider';
import { ErrorBoundary, ErrorDisplay } from '@/components/common/ErrorBoundary';
import { LoadingSpinner } from '@/components/ui/loading-spinner';

interface ImportPreviewProps {
  providerId: number;
  versionId: number;
  data: ParsedChannelData[];
  errors: ImportError[];
  isLoading?: boolean;
}

// Add error class implementations
class ImportValidationError extends Error implements ValidationError {
  type: 'validation';
  field: string;
  line: number;

  constructor(message: string, field: string, line: number) {
    super(message);
    this.type = 'validation';
    this.field = field;
    this.line = line;
    this.name = 'ValidationError';
  }
}

class ImportProcessError extends Error implements ProcessError {
  type: 'process';
  code: string;
  line: number;

  constructor(message: string, code: string, line: number) {
    super(message);
    this.type = 'process';
    this.code = code;
    this.line = line;
    this.name = 'ProcessError';
  }
}

export function ImportPreviewSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-6 w-24" />
      </div>
      <Skeleton className="h-[400px] w-full" />
      <div className="flex justify-between">
        <Skeleton className="h-10 w-24" />
        <Skeleton className="h-10 w-24" />
      </div>
    </div>
  );
}

export function ImportPreview({
  providerId,
  versionId,
  data,
  errors,
  isLoading
}: ImportPreviewProps) {
  const { 
    clearErrors,
    setPreviewData,
    addErrors,
    formData,
    resetImportState,
    currentStep
  } = useImportStore();
  
  // Debug logging to help identify issues
  useEffect(() => {
    console.log('ImportPreview component mounted with data:', { 
      dataLength: data?.length || 0, 
      hasData: !!data,
      currentStep,
      errors: errors?.length || 0
    });
    
    return () => {
      console.log('ImportPreview component unmounting');
    };
  }, [data, currentStep, errors]);
  
  const handleReset = useCallback(() => {
    resetImportState();
    clearErrors();
    setPreviewData(null);
  }, [resetImportState, clearErrors, setPreviewData]);

  const { data: categories } = useCategories(providerId, versionId);
  const { data: plans } = usePlans(providerId, versionId);
  const { data: types } = useChannelTypes(providerId);
  const { data: provider } = useProvider(providerId);

  // Enhanced error handling for data processing
  const processData = useCallback(async (rawData: ParsedChannelData[]) => {
    try {
      if (!provider?.has_sequence_numbers || !types) {
        return rawData;
      }

      const sequenceType = types.find(t => t.is_sequence);
      if (!sequenceType) {
        throw new Error('Sequential provider requires a sequence type');
      }

      // For preview, we'll show the next sequence numbers without actually importing
      const lastNumber = parseInt(formData?.lastNumber || '0', 10);
      let nextNumber = lastNumber + 1;

      // Return preview data with correct type structure
      return rawData.map(item => ({
        ...item,
        numbers: [{
          typeId: sequenceType.id,
          number: String(nextNumber++),
          isSequence: true,
          is_default: false, // Add required property
          isPreview: true // Keep preview flag for UI purposes
        } as ParsedChannelNumber] // Cast to ensure type compatibility
      })) as ParsedChannelData[]; // Cast the entire result to ensure type compatibility
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to process preview data");
      return rawData;
    }
  }, [provider?.has_sequence_numbers, types, formData?.lastNumber]);

  // Memoize category and plan maps
  const categoryMap = useMemo(() => {
    return new Map(categories?.map(c => [c.id, c.name]) || []);
  }, [categories]);

  const planMap = useMemo(() => {
    return new Map(plans?.map(p => [p.id, p.name]) || []);
  }, [plans]);

  // Group errors by line number
  const errorsByLine = useMemo(() => {
    const map = new Map<number, ImportError[]>();
    errors.forEach(error => {
      const existing = map.get(error.line) || [];
      map.set(error.line, [...existing, error]);
    });
    return map;
  }, [errors]);

  // Enhanced error grouping
  const errorsByType = useMemo(() => {
    return errors.reduce((acc, error) => {
      const type = error.type || 'other';
      acc[type] = acc[type] || [];
      acc[type].push(error);
      return acc;
    }, {} as Record<string, ImportError[]>);
  }, [errors]);

  // Enhanced error retry handler
  const handleRetry = async () => {
    try {
      clearErrors();
      
      if (!types) {
        throw new ImportValidationError('Channel types not loaded', 'types', 0);
      }

      const validationService = new ValidationService({
        providerId,
        versionId,
        isSequential: provider?.has_sequence_numbers ?? false,
        channelTypes: (types || []).map(type => ({
          ...type,
          display_name: type.name,
          is_default: type.is_default || false,
          is_sequence: type.is_sequence || false,
          provider_id: type.provider_id || 0,
          version_id: type.version_id || 0
        })),
        is_local: formData.is_local || false
      });

      // Validate data without importing
      const result = await validationService.validateData(data);
      
      if (result.isValid && result.data) {
        // Process and set preview data with correct typing
        const processedData = await processData(result.data);
        setPreviewData(processedData);
        
        if (result.warnings?.length) {
          toast.warning(`${result.warnings.length} warnings found. Check preview for details.`);
        } else {
          toast.success("Data is valid and ready for import");
        }
      } else {
        addErrors(result.errors);
      }
    } catch (error) {
      console.error('Retry validation failed:', error);
      
      if (error instanceof ImportValidationError) {
        toast.error(error.message);
      } else if (error instanceof ImportProcessError) {
        toast.error(error.message);
      } else {
        toast.error(error instanceof Error ? error.message : "An unexpected error occurred");
      }
    }
  };

  // Get selected category and plans from formData
  const selectedCategory = formData.categoryId ? categoryMap.get(formData.categoryId) : 'None';
  const selectedPlans = formData.planIds?.map(id => planMap.get(id)).filter(Boolean) || [];

  // Process the data when it changes
  const processedData = useMemo(() => {
    if (!data.length) return [];
    
    // Use the processData function and handle the Promise
    processData(data).catch(error => {
      console.error('Error processing data:', error);
      toast.error(error instanceof Error ? error.message : "Failed to process data");
    });

    return data;
  }, [data, processData]);

  // Modify the component's return statement to handle empty state
  if (!data?.length && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-8 space-y-4">
        <div className="text-center">
          <h3 className="text-lg font-medium">No Data to Preview</h3>
          <p className="text-sm text-muted-foreground">Please import data to continue</p>
        </div>
        <Button onClick={handleReset} variant="outline">
          Start New Import
        </Button>
      </div>
    );
  }

  // Add loading state handling
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <LoadingSpinner />
      </div>
    );
  }

  // Add error boundary wrapper
  return (
    <ErrorBoundary fallback={<ErrorDisplay error={errors[0]?.message || 'An error occurred'} />}>
      {isLoading ? (
        <ImportPreviewSkeleton />
      ) : (
        <div className="space-y-6">
          {/* Header with Status */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium">Preview Import Data</h3>
              <p className="text-sm text-muted-foreground">
                {data.length} channels will be imported
              </p>
            </div>
            <Badge variant={errors.length ? "destructive" : "default"}>
              {errors.length ? `${errors.length} Errors` : 'Valid'}
            </Badge>
          </div>

          {/* Enhanced error display */}
          {errors.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <div className="font-medium mb-2">Found {errors.length} issues:</div>
                {Object.entries(errorsByType).map(([type, typeErrors]) => (
                  <div key={type} className="mb-4">
                    <h4 className="font-medium mb-1 capitalize">{type} Errors:</h4>
                    <ul className="list-disc pl-4 space-y-1">
                      {typeErrors.map((error, index) => (
                        <li key={index} className="text-sm">
                          {error.line > 0 && <span className="font-mono">Line {error.line}: </span>}
                          {error.message}
                        </li>
                      ))}
                    </ul>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRetry}
                      className="mt-4"
                    >
                      Retry Validation
                    </Button>
                  </div>
                ))}
              </AlertDescription>
            </Alert>
          )}

          {/* Data Preview Table */}
          <ScrollArea className="h-[400px] border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[80px]">Line</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Numbers</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Plans</TableHead>
                  <TableHead className="w-[100px]">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {processedData.map((item) => {
                  const lineErrors = errorsByLine.get(item.line);
                  
                  return (
                    <TableRow 
                      key={item.line}
                      className={lineErrors ? 'bg-destructive/10' : undefined}
                    >
                      <TableCell>{item.line}</TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>
                        {item.numbers.map((n, idx) => (
                          <span key={idx}>
                            {types?.find(t => t.id === n.typeId)?.name}: {n.number || '*'}
                            {idx < item.numbers.length - 1 ? ', ' : ''}
                          </span>
                        ))}
                      </TableCell>
                      <TableCell>{selectedCategory}</TableCell>
                      <TableCell>{selectedPlans.join(', ')}</TableCell>
                      <TableCell>
                        {lineErrors ? (
                          <Badge variant="destructive">Error</Badge>
                        ) : (
                          <Badge variant="default">Valid</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </ScrollArea>
        </div>
      )}
    </ErrorBoundary>
  );
}
